// Runtime id-binding hook: turns a static `deps(...)` declaration into live data.
// The caller binds only owner ids (e.g. `{ shipId, dayId }`) — no endpoint, sub
// key or merge. For each relationship the hook, for the lifetime of the mount:
//   1. retains its entity type (ref-counted, first retain subscribes the store's
//      single live handler for that type);
//   2. fetches its slice (deduped by key) so it is normalized into the store;
//   3. selects it back out via the store's stable snapshots, so the component
//      re-renders only when that slice changes.
// The relationship's query filter is derived from the schema edge linking the
// declaration's root to the related entity (e.g. Day→Log = `dayId`).
//
// #1707 (tracker #1677) moved this mechanism into app-base's generic
// `data/use-deps` (parameterized by a `DataSchema`, taking its store explicitly);
// this wrapper resolves the ambient store via `useDataStore()` (Logbook's own
// binding, `./Context`) so every existing `useDeps(declaration, ids)` call site
// (no store argument) is unaffected.
import { useDataStore } from "./Context"
import { useDeps as genericUseDeps, type UseDepsOptions } from "app-base/data/use-deps"
import type { DepsIds as GenericDepsIds } from "app-base/data/use-deps"
import type { Deps, DepsResult, Relationship } from "./Deps"
import type { LogbookSchema } from "./Connections"
import type { SchemaEntity } from "../Types/Generated/LogbookModel/Schema"

export type { UseDepsOptions }

// The owner ids a declaration can be bound to: the FK fields shared by every one of its
// relationship types. `useDeps` passes the ids to each relationship's fetch, so an id one
// of them can't be filtered by would narrow nothing there — binding it is a compile error
// rather than a silently wrong slice. (A union entity type collapses to its common numeric
// FK fields, which is exactly the intersection this needs.)
export type DepsIds<R extends Record<string, Relationship<SchemaEntity>>> = GenericDepsIds<LogbookSchema, R>;

export function useDeps<R extends Record<string, Relationship<SchemaEntity>>>(
  declaration: Deps<R>,
  ids: DepsIds<R>,
  options: UseDepsOptions = {},
): DepsResult<R> {
  const store = useDataStore();
  const fetched = Object.values(declaration.relationships).every((relationship) =>
    store.isQueryFetched(relationship.type, ids as never));
  return genericUseDeps(store, declaration, ids, {
    ...options,
    fetch: options.fetch !== false && !fetched,
  });
}
